import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { lazy, Suspense } from 'react';
import { Loading } from '@emfit/shared-ui';
import { Main } from './components/Main';
import { LoginPage } from './pages';
import { ProtectedRoute } from './components/ProtectedRoute';

// All views lazy-loaded: each route's JS is only parsed on first visit.
// This keeps the initial bundle small and avoids parsing Highcharts (GroupDashboard,
// GroupPatterns) until the user actually navigates there.
const lazyView = <
  T extends Record<K, React.ComponentType<any>>,
  K extends string,
>(
  loader: () => Promise<T>,
  key: K
): React.LazyExoticComponent<T[K]> =>
  lazy(() => loader().then((m) => ({ default: m[key] })));

const DeviceListView = lazyView(
  () => import('./views/DeviceListView'),
  'DeviceListView'
);
const GroupDashboardView = lazyView(
  () => import('./views/GroupDashboardView'),
  'GroupDashboardView'
);
const PermissionsView = lazyView(
  () => import('./views/PermissionsView'),
  'PermissionsView'
);
const GroupPatternsView = lazyView(
  () => import('./views/GroupPatternsView'),
  'GroupPatternsView'
);
const CreateAccountView = lazyView(
  () => import('./views/CreateAccountView'),
  'CreateAccountView'
);
const SettingsView = lazyView(
  () => import('./views/SettingsView'),
  'SettingsView'
);
const DeviceDetailView = lazyView(
  () => import('./views/DeviceDetailView'),
  'DeviceDetailView'
);
const EventLogView = lazyView(
  () => import('./views/EventLogView'),
  'EventLogView'
);
const DeviceDetailOverview = lazyView(
  () => import('./views/DeviceDetailView/DeviceDetailOverview'),
  'DeviceDetailOverview'
);
const DeviceDetailEvents = lazyView(
  () => import('./views/DeviceDetailView/DeviceDetailEvents'),
  'DeviceDetailEvents'
);
const DeviceDetailSettings = lazyView(
  () => import('./views/DeviceDetailView/DeviceDetailSettings'),
  'DeviceDetailSettings'
);
const DeviceDetailDeviceSettings = lazyView(
  () => import('./views/DeviceDetailView/DeviceDetailDeviceSettings'),
  'DeviceDetailDeviceSettings'
);
const MonitorView = lazyView(
  () => import('./views/MonitorView'),
  'MonitorView'
);
const AuditLogView = lazyView(
  () => import('./views/AuditLogView'),
  'AuditLogView'
);

const fallback = (
  <div className="flex justify-center items-center min-h-[400px]">
    <Loading />
  </div>
);

const DeviceDetailOverviewRoute = observer(() => {
  const { deviceId } = useParams<{ deviceId: string }>();
  return deviceId ? <DeviceDetailOverview deviceId={deviceId} /> : null;
});
const DeviceDetailEventsRoute = observer(() => {
  const { deviceId } = useParams<{ deviceId: string }>();
  return deviceId ? <DeviceDetailEvents deviceId={deviceId} /> : null;
});
const DeviceDetailSettingsRoute = observer(() => {
  const { deviceId } = useParams<{ deviceId: string }>();
  return deviceId ? <DeviceDetailSettings deviceId={deviceId} /> : null;
});
const DeviceDetailDeviceSettingsRoute = observer(() => {
  const { deviceId } = useParams<{ deviceId: string }>();
  return deviceId ? <DeviceDetailDeviceSettings deviceId={deviceId} /> : null;
});

function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/create-account"
          element={
            <Suspense fallback={fallback}>
              <CreateAccountView />
            </Suspense>
          }
        />

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <Main />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/monitor" replace />} />
          <Route
            path="trends"
            element={
              <Suspense fallback={fallback}>
                <GroupPatternsView />
              </Suspense>
            }
          />
          <Route
            path="monitor"
            element={
              <Suspense fallback={fallback}>
                <MonitorView />
              </Suspense>
            }
          />
          <Route
            path="devices"
            element={
              <Suspense fallback={fallback}>
                <DeviceListView />
              </Suspense>
            }
          />
          <Route
            path="devices/:deviceId"
            element={
              <Suspense fallback={fallback}>
                <DeviceDetailView />
              </Suspense>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route
              path="overview"
              element={
                <Suspense fallback={fallback}>
                  <DeviceDetailOverviewRoute />
                </Suspense>
              }
            />
            <Route
              path="events"
              element={
                <Suspense fallback={fallback}>
                  <DeviceDetailEventsRoute />
                </Suspense>
              }
            />
            <Route
              path="notification-settings"
              element={
                <Suspense fallback={fallback}>
                  <DeviceDetailSettingsRoute />
                </Suspense>
              }
            />
            <Route
              path="device-settings"
              element={
                <Suspense fallback={fallback}>
                  <DeviceDetailDeviceSettingsRoute />
                </Suspense>
              }
            />
            <Route
              path="settings"
              element={
                <Suspense fallback={fallback}>
                  <DeviceDetailSettingsRoute />
                </Suspense>
              }
            />
          </Route>
          <Route
            path="dashboard"
            element={
              <Suspense fallback={fallback}>
                <GroupDashboardView />
              </Suspense>
            }
          />
          <Route
            path="events"
            element={
              <Suspense fallback={fallback}>
                <EventLogView />
              </Suspense>
            }
          />
          <Route
            path="users"
            element={
              <ProtectedRoute masterOnly>
                <Suspense fallback={fallback}>
                  <PermissionsView />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="audit-log"
            element={
              <ProtectedRoute masterUserOnly>
                <Suspense fallback={fallback}>
                  <AuditLogView />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="settings"
            element={
              <Suspense fallback={fallback}>
                <SettingsView />
              </Suspense>
            }
          />
          {/* Redirects for removed top-level routes */}
          <Route
            path="device-manager"
            element={<Navigate to="/dashboard" replace />}
          />
          <Route
            path="permissions"
            element={<Navigate to="/users" replace />}
          />
          <Route path="*" element={<Navigate to="/monitor" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default observer(App);
